import * as firebase from 'firebase/app';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  inMemoryPersistence,
  signOut,
} from 'firebase/auth';
import { getUser } from '../utils/api';
import apiClient from '../utils/apiClient';
import {
  enrolTotpIfNeeded,
  MFA_REQUIRED_ERROR_CODE,
  resolveTotpSignIn,
} from './twoFactorAuthentication';

import {
  STORAGE_USER_LOGGED_IN,
  STORAGE_USER_TYPE,
  STORAGE_USER_NAME,
  LOGIN_METHOD_PASSWORD,
  storeLoginMethod,
} from '../utils/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_AUTH_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
};

export async function setupFirebaseAuthentication() {
  const firebaseApps = firebase.getApps();
  const isFirebaseInitialized = firebaseApps.length > 0;
  if (!isFirebaseInitialized) {
    initializeApp(firebaseConfig);
  }
  await getAuth().setPersistence(inMemoryPersistence);
}
/**
 * Exchanges a Firebase ID token for the backend session cookie
 * @param {string} idToken
 */
async function createBackendSession(idToken) {
  const formData = new FormData();
  formData.append('idToken', idToken);

  await apiClient.post('session/login', formData, {
    'Content-Type': 'multipart/form-data',
    withCredentials: true,
  });
  storeLoginMethod(LOGIN_METHOD_PASSWORD);
}

async function signInWithFirstAndSecondFactor(
  auth,
  username,
  password,
  requestSignInCode,
) {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      username,
      password,
    );
    return userCredential.user;
  } catch (error) {
    if (error.code === MFA_REQUIRED_ERROR_CODE && requestSignInCode) {
      return resolveTotpSignIn(auth, error, requestSignInCode);
    }
    throw error;
  }
}

/**
 * @param {string} username
 * @param {string} password
 * @param {object} twoFactorPrompts UI callbacks used when 2FA is needed, see
 *   resolveTotpSignIn and enrolTotpIfNeeded
 */
export async function signInWithEmailPassword(
  username,
  password,
  twoFactorPrompts = {},
) {
  const { requestSignInCode, requestEnrolmentCode } = twoFactorPrompts;
  const auth = getAuth();
  try {
    const user = await signInWithFirstAndSecondFactor(
      auth,
      username,
      password,
      requestSignInCode,
    );
    if (user) {
      const justEnrolled = requestEnrolmentCode
        ? await enrolTotpIfNeeded(user, requestEnrolmentCode)
        : false;

      // Refresh after enrolling so the token carries the second factor claim
      await createBackendSession(await user.getIdToken(justEnrolled));

      await signOut(auth);

      return new Promise((resolve, reject) => {
        getUser()
          .then((response) => {
            localStorage.setItem(STORAGE_USER_LOGGED_IN, 'true');
            localStorage.setItem(STORAGE_USER_NAME, response.data.userName);
            localStorage.setItem(
              STORAGE_USER_TYPE,
              JSON.stringify(response.data.roles),
            );
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    }
    return Promise.resolve(null);
  } catch (error) {
    // Don't keep a half signed in Firebase user around, e.g. after cancelling 2FA
    await signOut(auth).catch(() => {});
    return Promise.reject(error);
  }
}

/**
 * Confirms the password (and 2FA code when enrolled) of the logged in user, because
 * Firebase only allows changing 2FA right after a sign in. Call
 * finishTwoFactorSettings when done.
 * @param {string} email
 * @param {string} password
 * @param {function} requestSignInCode see resolveTotpSignIn
 * @returns {Promise<object>} the signed in Firebase user
 */
export async function signInForTwoFactorSettings(
  email,
  password,
  requestSignInCode,
) {
  await setupFirebaseAuthentication();
  const auth = getAuth();
  try {
    return await signInWithFirstAndSecondFactor(
      auth,
      email,
      password,
      requestSignInCode,
    );
  } catch (error) {
    await signOut(auth).catch(() => {});
    throw error;
  }
}

/**
 * Signs out of Firebase after changing 2FA. After enrolling, renews the backend
 * session so it carries the second factor claim.
 * @param {object} user Firebase user from signInForTwoFactorSettings
 * @param {boolean} renewSession
 */
export async function finishTwoFactorSettings(user, renewSession) {
  try {
    if (user && renewSession) {
      await createBackendSession(await user.getIdToken(true));
    }
  } finally {
    await signOut(getAuth()).catch(() => {});
  }
}
